// src/pages/ItemRequest.js
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { collection, addDoc, serverTimestamp, getDocs, doc, getDoc } from "firebase/firestore";
import { db, auth } from "../services/firebase";
import { useUserData } from "../hooks/useUserData";
import { scheduleReturnReminder, createBorrowRequestNotification } from "../services/notifications";
import NavbarMobile from "../components/NavbarMobile";
import BottomNav from "../components/BottomNav";

export default function ItemRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const [requestType, setRequestType] = useState("Borrow");
  const [assetName, setAssetName] = useState("");
  const [urgency, setUrgency] = useState("Normal");
  const [borrowingDate, setBorrowingDate] = useState("");
  const [borrowTime, setBorrowTime] = useState("");
  const [returnTime, setReturnTime] = useState("");
  const [reason, setReason] = useState("");
  const [destination, setDestination] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [availableAssets, setAvailableAssets] = useState([]);

  const user = auth.currentUser;
  const userId = user?.uid;
  const userEmail = user?.email;



  // Destination locations for BORROW requests only
  const destinationLocations = [
    { id: "blsb-prop-custodian", name: "BLSB - Property Custodian" },
    { id: "blsb-room-1", name: "BLSB - Room 1" },
    { id: "blsb-room-2", name: "BLSB - Room 2" },
    { id: "blsb-room-3", name: "BLSB - Room 3" },
    { id: "blsb-room-4", name: "BLSB - Room 4" },
    { id: "blsb-room-5", name: "BLSB - Room 5" },
    { id: "blsb-room-6", name: "BLSB - Room 6" },
    { id: "blsb-room-7", name: "BLSB - Room 7" },
    { id: "blsb-room-8", name: "BLSB - Room 8" },
    { id: "blsb-room-9", name: "BLSB - Room 9" },
    { id: "dpwh-canteen", name: "DPWH - Canteen" },
    { id: "dpwh-guidance", name: "DPWH - Guidance" },
    { id: "dpwh-registrar", name: "DPWH - Registrar" },
    { id: "dpwh-room-2", name: "DPWH - Room 2" },
    { id: "dpwh-room-3", name: "DPWH - Room 3" },
    { id: "dpwh-room-4", name: "DPWH - Room 4" },
    { id: "dpwh-room-5", name: "DPWH - Room 5" },
    { id: "dpwh-room-6", name: "DPWH - Room 6" },
    { id: "dpwh-room-7", name: "DPWH - Room 7" },
    { id: "dpwh-room-8", name: "DPWH - Room 8" },
    { id: "dpwh-storage", name: "DPWH - Storage" },
    { id: "ppp-euler", name: "PPP - Euler" },
    { id: "ppp-ict-room-2", name: "PPP - ICT Room 2" },
    { id: "ppp-ict-room-3", name: "PPP - ICT Room 3" },
    { id: "ppp-principal", name: "PPP - Principal" }
  ];

  // Fetch list of available assets for the dropdown (only show assets that are available)
  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const assetsCol = collection(db, "assets");
        const assetSnapshot = await getDocs(assetsCol);
        const assetsData = assetSnapshot.docs
          .map(doc => ({
            id: doc.id,
            name: doc.data().name || "Unnamed Asset",
            category: doc.data().category || "General",
            currentLocation: doc.data().location || "Unknown",
            status: doc.data().status || "available",
          }))
          .filter(asset => {
            // Only show assets that are available (not borrowed, in-use, under maintenance, or missing)
            const status = asset.status.toLowerCase();
            return status === "available";
          });
        setAvailableAssets(assetsData);
      } catch (error) {
        console.error("Error fetching assets:", error);
        setMessage("❌ Failed to load assets. Please try again.");
      }
    };
    fetchAssets();
  }, []);

  // Pre-select asset if coming from BrowseAssets page
  useEffect(() => {
    if (location.state?.selectedAssetId && availableAssets.length > 0) {
      const selectedAsset = availableAssets.find(asset => asset.id === location.state.selectedAssetId);
      if (selectedAsset) {
        setAssetName(selectedAsset.name);
      }
    }
  }, [location.state, availableAssets]);

  const validateForm = () => {
    if (!assetName) return "Please select an asset.";
    if (!reason.trim()) return "Please provide a reason/details.";

    // ✅ ONLY require destination and scheduling for Borrow requests
    if (requestType === "Borrow") {
      if (!destination) return "Please select a destination location.";
      if (!borrowingDate) return "Please specify the borrowing date.";
      if (!borrowTime) return "Please specify the time to borrow.";
      if (!returnTime) return "Please specify the time to return.";

      // Strict validation: No same-day borrowing allowed
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of today
      const selectedDate = new Date(borrowingDate);
      selectedDate.setHours(0, 0, 0, 0); // Set to start of selected date

      if (selectedDate <= today) return "Borrowing date must be in the future (no same-day borrowing).";

      // Validate that return time is after borrow time
      if (borrowTime >= returnTime) return "Return time must be later than borrow time.";

      // Validate that return time is on or before 5:00 PM
      if (returnTime > "17:00") return "Return time must be on or before 5:00 PM.";
    }

    // ✅ Maintenance requests don't need destination
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setMessage(`❌ ${validationError}`);
      return;
    }

    setLoading(true);
    setMessage("");

    const assetData = availableAssets.find(a => a.name === assetName);
    const selectedDestination = destinationLocations.find(loc => loc.id === destination);

    // Fetch current user profile data
    let userProfile = {};
    try {
      const userDoc = await getDoc(doc(db, "users", userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        userProfile = {
          studentFirstName: userData.firstName || "",
          studentMiddleName: userData.middleName || "",
          studentSurname: userData.surname || "",
          studentLrn: userData.lrn || "",
          studentGradeYearLevel: userData.gradeYearLevel || "",
          studentContactNumber: userData.contactNumber || "",
        };
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }

    const payload = {
      requesterId: userId,
      userEmail,
      assetName,
      assetId: assetData ? assetData.id : 'N/A',
      type: requestType.toLowerCase(),
      urgency,
      reason,
      status: "pending",
      createdAt: serverTimestamp(),
      requesterName: user?.displayName || userEmail?.split('@')[0] || 'User',
      assetCategory: assetData?.category || 'General',
      lastUpdated: serverTimestamp(),
      currentLocation: assetData?.currentLocation || "Unknown",
      ...userProfile, // Include student profile information
    };

    // ✅ Only add destination and moveRequest for Borrow type
    if (requestType === "Borrow") {
      payload.destinationId = destination;
      payload.destinationName = selectedDestination ? selectedDestination.name : destination;
      payload.moveRequest = true;
      payload.borrowingDate = borrowingDate;
      payload.borrowTime = borrowTime;
      payload.returnTime = returnTime;
      payload.submissionTimestamp = serverTimestamp();
    } else {
      // ✅ Maintenance requests - no destination needed
      payload.moveRequest = false;
    }

    try {
      const docRef = await addDoc(collection(db, "requests"), payload);
      console.log("Request submitted with ID:", docRef.id);

      // Create notification for admin if it's a borrow request
      if (requestType === "Borrow") {
        const requestDataWithId = { ...payload, id: docRef.id };
        await createBorrowRequestNotification(requestDataWithId);
      }

      setMessage(`✅ ${requestType} Request submitted successfully!`);

      // Clear form
      setAssetName("");
      setUrgency("Normal");
      setBorrowingDate("");
      setBorrowTime("");
      setReturnTime("");
      setReason("");
      setDestination("");

      setTimeout(() => navigate('/requests'), 1500);
    } catch (err) {
      console.error("Request submission failed:", err);
      setMessage("❌ Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Get current location of selected asset
  const getCurrentLocation = () => {
    const assetData = availableAssets.find(a => a.name === assetName);
    return assetData?.currentLocation || "Unknown";
  };

  return (
    <div className="pb-20 bg-gray-50 min-h-screen">
      <div className="p-4 pt-20 max-w-lg mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-4">
          <h2 className="text-xl font-bold text-center mb-4 text-orange-600">
            {requestType === "Borrow" ? "Submit Move Request" : "Submit Maintenance Request"}
          </h2>
          
          {/* Request Type Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1 mb-4">
            <button
              type="button"
              onClick={() => {
                setRequestType("Borrow");
                setMessage("");
              }}
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors ${
                requestType === "Borrow" ? "bg-blue-600 text-white shadow-md" : "text-gray-700 hover:bg-gray-200"
              }`}
            >
              Borrow Asset
            </button>
            <button
              type="button"
              onClick={() => {
                setRequestType("Maintenance");
                setMessage("");
                setBorrowingDate("");
                setBorrowTime("");
                setReturnTime("");
                setDestination(""); // Clear destination for maintenance
              }}
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-colors ${
                requestType === "Maintenance" ? "bg-red-500 text-white shadow-md" : "text-gray-700 hover:bg-gray-200"
              }`}
            >
              Report Issue/Maintenance
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Asset Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asset Name *</label>
              <select
                value={assetName}
                onChange={(e) => setAssetName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Select Asset</option>
                {availableAssets.map(asset => (
                  <option key={asset.id} value={asset.name}>
                    {asset.name} {asset.currentLocation ? `(Currently in ${asset.currentLocation})` : ''}
                  </option>
                ))}
              </select>
              {assetName && (
                <p className="text-xs text-gray-500 mt-1">
                  Current location: <span className="font-medium">{getCurrentLocation()}</span>
                </p>
              )}
            </div>

            {/* ✅ Destination Location - ONLY for Borrow requests */}
            {requestType === "Borrow" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Move To (Destination) *
                </label>
                <select
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                  required={requestType === "Borrow"}
                >
                  <option value="">Select Destination</option>
                  {destinationLocations.map(location => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Where should this asset be moved to?
                </p>
              </div>
            )}

            {/* Urgency Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Urgency Level *</label>
              <select
                value={urgency}
                onChange={(e) => setUrgency(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="Normal">Normal</option>
                <option value="High">High (Needs immediate attention)</option>
                <option value="Critical">Critical (Device unusable)</option>
              </select>
            </div>

            {/* Borrow Specific Fields */}
            {requestType === "Borrow" && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Borrowing Date *</label>
                  <input
                    type="date"
                    value={borrowingDate}
                    onChange={(e) => setBorrowingDate(e.target.value)}
                    min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                    onKeyDown={(e) => e.preventDefault()} // Prevent manual typing
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Must be scheduled in advance (no same-day borrowing)
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time to Borrow *</label>
                    <input
                      type="time"
                      value={borrowTime}
                      onChange={(e) => setBorrowTime(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time to Return *</label>
                    <input
                      type="time"
                      value={returnTime}
                      onChange={(e) => setReturnTime(e.target.value)}
                      max="17:00"
                      className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                </div>
                <p className="text-xs text-orange-600 font-medium">
                  ⚠️ Return time must be later than borrow time and on or before 5:00 PM
                </p>
              </div>
            )}
            
            {/* Reason / Details Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason / Details *</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={
                  requestType === "Borrow" 
                    ? "Explain why you need to borrow this item and move it to the selected location..." 
                    : "Describe the maintenance issue or damage here. A technician will be assigned to fix it."
                }
                rows="4"
                className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 rounded-lg font-semibold transition ${
                loading ? "bg-gray-400 cursor-not-allowed" : "bg-orange-600 hover:bg-orange-700"
              } text-white shadow-lg`}
            >
              {loading ? "Submitting..." : `Submit ${requestType} Request`}
            </button>
          </form>

          {message && (
            <div className={`mt-4 p-3 rounded-lg text-center ${
              message.startsWith('✅') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {message}
            </div>
          )}

          <div className="text-xs text-gray-500 mt-4">
            <p><strong>Note:</strong></p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              {requestType === "Borrow" ? (
                <>
                  <li>Borrow requests require destination location selection</li>
                  <li>Admin will physically move the asset to the selected destination</li>
                  <li>Assets must be returned on the same day by 5:00 PM</li>
                  <li>Return the asset to the designated location and scan with your student scanner</li>
                  <li>All requests require admin approval</li>
                </>
              ) : (
                <>
                  <li>Maintenance requests will be assigned to a technician</li>
                  <li>Technician will inspect and repair the asset in its current location</li>
                  <li>No physical movement required for maintenance requests</li>
                  <li>All requests require admin approval</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>

    </div>
  );
}
